import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  addNewDoc,
  setDocData,
  queryAllDocsByFieldSnap,
  getAllDocsSnap,
} from "../../../utils/firebase/Firebase.utils";
import InventoryFormAdd from "./InventoryFormAdd";
import TransactionCard from "./TransactionCard";
import InputBox from "../Components/InputBox";
import toast from "react-hot-toast";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  FiPackage,
  FiPlus,
  FiArrowUpRight,
  FiShoppingBag,
  FiBarChart2,
  FiList,
  FiX,
  FiCalendar,
  FiTag,
  FiClipboard,
  FiBox,
  FiTrendingUp,
  FiTrendingDown,
  FiUser,
  FiSend,
  FiPieChart,
} from "react-icons/fi";
import SelectInput from "../Components/SelectInput";

export default function ItemDetailsPage({ data, setViewMode }) {
  const [addMode, setAddMode] = useState(false);
  const [issuanceMode, setIssuanceMode] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [actionType, setActionType] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("transactions");
  const [formData, setFormData] = useState({});
  const [ledgerData, setLedgerData] = useState({
    additions: 0,
    issuances: 0,
    balance: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [issuedToUsers, setIssuedToUsers] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  const currentUser = useSelector((state) => state.userObject);

  // Colors based on requirements
  const primaryColor = "#363062";
  const secondaryColor = "#8FCD00";

  useEffect(() => {
    const fetchTransactions = async () => {
      await queryAllDocsByFieldSnap(
        "inventoryTransactions",
        "itemId",
        "==",
        data.id,
        (data) => {
          setTransactions(data || []);
          calculateLedgerData(data);
          prepareChartData(data);
          setLoading(false);
        }
      );
    };
    fetchTransactions();
  }, [data.id]);

  useEffect(() => {
    const fetchAdminUsers = async () => {
      setLoadingUsers(true);
      await getAllDocsSnap("users", (data) => {
        const filteredUsers = data.filter(
          (user) => !user.roles?.includes("user")
        );
        setAdminUsers(filteredUsers);
        setLoadingUsers(false);
      });
    };

    if (issuanceMode) {
      fetchAdminUsers();
    }
  }, [issuanceMode]);

  const calculateLedgerData = (transactionData) => {
    let additions = 0;
    let issuances = 0;

    transactionData.forEach((transaction) => {
      if (transaction.transactionType === "addition") {
        additions += Number(transaction.quantity);
      } else if (transaction.transactionType === "issuance") {
        issuances += Number(transaction.quantity);
      }
    });

    setLedgerData({
      additions,
      issuances,
      balance: additions - issuances,
    });
  };

  const prepareChartData = (transactionData) => {
    // Group transactions by month for chart data
    const monthlyUsage = {};

    transactionData.forEach((transaction) => {
      const date = new Date(transaction.date || transaction.createdAt);
      const monthYear = `${date.getMonth() + 1}/${date.getFullYear()}`;

      if (!monthlyUsage[monthYear]) {
        monthlyUsage[monthYear] = {
          name: monthYear,
          additions: 0,
          issuances: 0,
        };
      }

      if (transaction.transactionType === "addition") {
        monthlyUsage[monthYear].additions += Number(transaction.quantity);
      } else if (transaction.transactionType === "issuance") {
        monthlyUsage[monthYear].issuances += Number(transaction.quantity);
      }
    });

    // Convert to array for the chart
    setChartData(Object.values(monthlyUsage));
  };

  const handleAddMode = (action) => {
    if (action === "add") {
      setAddMode(true);
      setIssuanceMode(false);
    } else if (action === "issuance") {
      setIssuanceMode(true);
      setAddMode(false);
    }
    setActionType(action);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleIssuanceSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    if (Number(formData.quantity) > ledgerData.balance) {
      toast.error("Cannot issue more than available balance");
      setIsProcessing(false);
      return;
    }

    const issuanceData = {
      ...formData,
      transactionType: "issuance",
      itemId: data.id,
      itemName: data.itemName || "Unnamed Item",
      user: currentUser.displayName,
      userId: currentUser.uid,
      createdAt: new Date().toLocaleString(),
      userEmail: currentUser.email,
      updateType: "issuance of inventory",
      date: formData.date || new Date().toLocaleDateString(),
      issuedTo: issuedToUsers.map((user) => user.label).join(", "),
      issuedToUsers: issuedToUsers,
    };

    // Only add these fields if they exist to prevent 'undefined' values in Firestore
    if (data.itemCode) issuanceData.itemCode = data.itemCode;
    if (data.category) issuanceData.category = data.category;

    try {
      await addNewDoc("inventoryTransactions", issuanceData);
      toast.success("Issuance recorded successfully");
      setFormData({});
      setIssuedToUsers([]);
      setIssuanceMode(false);

      // Refresh transactions
      await queryAllDocsByFieldSnap(
        "inventoryTransactions",
        "itemId",
        "==",
        data.id,
        (newData) => {
          setTransactions(newData || []);
          calculateLedgerData(newData);
          prepareChartData(newData);
        }
      );
    } catch (error) {
      toast.error("Failed to record issuance");
      console.error(error);
    } finally {
      setIsProcessing(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "transactions":
        return (
          <div className="w-full">
            {loading ? (
              <div className="text-center p-8 flex items-center justify-center">
                <div className="animate-pulse flex flex-col items-center">
                  <div className="h-12 w-12 rounded-full bg-gray-200 mb-4"></div>
                  <div className="h-4 w-40 bg-gray-200 rounded"></div>
                </div>
              </div>
            ) : transactions.length > 0 ? (
              <div className="space-y-3">
                {transactions.map((transaction, index) => (
                  <div
                    key={index}
                    className="border border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <TransactionCard data={transaction} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center p-8 flex flex-col items-center justify-center text-gray-500">
                <FiClipboard className="text-3xl mb-2" />
                <p>No transactions recorded yet</p>
                <button
                  onClick={() => handleAddMode("add")}
                  className="mt-4 px-4 py-2 rounded-md text-white flex items-center gap-2"
                  style={{ backgroundColor: primaryColor }}
                >
                  <FiPlus /> Add First Transaction
                </button>
              </div>
            )}
          </div>
        );

      case "ledger":
        return (
          <div className="w-full">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="rounded-lg p-4 shadow-sm flex items-center border border-gray-100">
                <div
                  className="p-3 rounded-full mr-4"
                  style={{ backgroundColor: `${secondaryColor}20` }}
                >
                  <FiTrendingUp
                    className="text-xl"
                    style={{ color: secondaryColor }}
                  />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600">Total Additions</h3>
                  <p className="text-lg font-bold">{ledgerData.additions}</p>
                </div>
              </div>
              <div className="rounded-lg p-4 shadow-sm flex items-center border border-gray-100">
                <div
                  className="p-3 rounded-full mr-4"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <FiTrendingDown
                    className="text-xl"
                    style={{ color: primaryColor }}
                  />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600">Total Issuances</h3>
                  <p className="text-lg font-bold">{ledgerData.issuances}</p>
                </div>
              </div>
              <div className="rounded-lg p-4 shadow-sm flex items-center border border-gray-100">
                <div
                  className="p-3 rounded-full mr-4"
                  style={{ backgroundColor: `${primaryColor}20` }}
                >
                  <FiPackage
                    className="text-xl"
                    style={{ color: primaryColor }}
                  />
                </div>
                <div>
                  <h3 className="text-sm text-gray-600">Current Balance</h3>
                  <p className="text-lg font-bold">{ledgerData.balance}</p>
                </div>
              </div>
            </div>

            <div className="border border-gray-100 rounded-lg p-4 shadow-sm">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-semibold text-gray-700 flex items-center gap-2">
                  <FiList /> Detailed Ledger
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: `${primaryColor}10` }}>
                      <th className="p-2 text-left text-xs font-medium text-gray-600 rounded-l-md">
                        <div className="flex items-center gap-1">
                          <FiCalendar size={14} />
                          <span>DATE</span>
                        </div>
                      </th>
                      <th className="p-2 text-left text-xs font-medium text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiTag size={14} />
                          <span>TYPE</span>
                        </div>
                      </th>
                      <th className="p-2 text-left text-xs font-medium text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiPackage size={14} />
                          <span>QTY</span>
                        </div>
                      </th>
                      <th className="p-2 text-left text-xs font-medium text-gray-600">
                        <div className="flex items-center gap-1">
                          <FiUser size={14} />
                          <span>USER</span>
                        </div>
                      </th>
                      <th className="p-2 text-left text-xs font-medium text-gray-600 rounded-r-md">
                        <div className="flex items-center gap-1">
                          <FiClipboard size={14} />
                          <span>NOTES</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((transaction, index) => (
                      <tr
                        key={index}
                        className={`border-b ${
                          index === transactions.length - 1 ? "border-b-0" : ""
                        }`}
                      >
                        <td className="p-3 text-sm">
                          {transaction.date || transaction.createdAt}
                        </td>
                        <td className="p-3 text-sm">
                          <span
                            className="inline-flex items-center px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor:
                                transaction.transactionType === "addition"
                                  ? `${secondaryColor}20`
                                  : `${primaryColor}20`,
                              color:
                                transaction.transactionType === "addition"
                                  ? secondaryColor
                                  : primaryColor,
                            }}
                          >
                            {transaction.transactionType === "addition" ? (
                              <FiPlus className="mr-1" size={10} />
                            ) : (
                              <FiArrowUpRight className="mr-1" size={10} />
                            )}
                            {transaction.transactionType}
                          </span>
                        </td>
                        <td className="p-3 text-sm font-medium">
                          {transaction.quantity}
                        </td>
                        <td className="p-3 text-sm">{transaction.user}</td>
                        <td className="p-3 text-sm text-gray-600">
                          {transaction.remarks || transaction.issuedTo || "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );

      case "chart":
        return (
          <div className="w-full">
            <div className="border border-gray-100 rounded-lg p-4 shadow-sm mb-6">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-4">
                <FiBarChart2 /> Inventory Usage Over Time
              </h3>

              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <BarChart data={chartData}>
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        borderColor: "#e5e7eb",
                        borderRadius: "0.375rem",
                      }}
                    />
                    <Legend />
                    <Bar
                      dataKey="additions"
                      fill={secondaryColor}
                      name="Additions"
                    />
                    <Bar
                      dataKey="issuances"
                      fill={primaryColor}
                      name="Issuances"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="border border-gray-100 rounded-lg p-4 shadow-sm">
              <h3 className="font-semibold text-gray-700 flex items-center gap-2 mb-4">
                <FiPieChart /> Current Distribution
              </h3>
              <div style={{ width: "100%", height: 300 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={[
                        {
                          name: "In Stock",
                          value:
                            ledgerData.balance > 0 ? ledgerData.balance : 0,
                        },
                        {
                          name: "Issued",
                          value:
                            ledgerData.issuances > 0 ? ledgerData.issuances : 0,
                        },
                      ]}
                      cx="50%"
                      cy="50%"
                      labelLine={true}
                      label={({ name, percent }) =>
                        `${name}: ${(percent * 100).toFixed(0)}%`
                      }
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      <Cell fill={secondaryColor} />
                      <Cell fill={primaryColor} />
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "white",
                        borderColor: "#e5e7eb",
                        borderRadius: "0.375rem",
                      }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="page relative flex flex-col gap-6">
      <div className="flex justify-between items-center p-4 bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="flex items-center gap-3">
          <div
            className="p-3 rounded-full"
            style={{ backgroundColor: `${primaryColor}10` }}
          >
            <FiBox className="text-xl" style={{ color: primaryColor }} />
          </div>
          <div>
            <h1 className="text-lg font-bold text-gray-800">{data.itemName}</h1>
            <p className="text-sm text-gray-500">
              ID:{" "}
              {data.itemCode || (data.id && data.id.substring(0, 8)) || "N/A"}
            </p>
          </div>
        </div>
        <button
          className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          onClick={() => setViewMode(false)}
        >
          <FiX className="text-gray-500" />
        </button>
      </div>

      <div className="flex flex-col lg:flex-row items-start gap-4">
        <div className="lg:w-1/3 w-full">
          <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-100">
            <h2 className="text-sm font-semibold text-gray-600 uppercase mb-4 flex items-center gap-2">
              <FiShoppingBag style={{ color: primaryColor }} /> Item Information
            </h2>

            <div className="space-y-4">
              <div
                className="flex items-center justify-between p-3 rounded-md"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <div className="flex items-center gap-2">
                  <FiTag className="text-gray-500" />
                  <span className="text-sm text-gray-600">Category</span>
                </div>
                <span className="text-sm font-medium">
                  {data.category || "Uncategorized"}
                </span>
              </div>

              <div
                className="flex items-center justify-between p-3 rounded-md"
                style={{ backgroundColor: `${secondaryColor}10` }}
              >
                <div className="flex items-center gap-2">
                  <FiPackage className="text-gray-500" />
                  <span className="text-sm text-gray-600">Current Stock</span>
                </div>
                <span className="text-sm font-medium">
                  {ledgerData.balance} units
                </span>
              </div>

              <div
                className="flex items-center justify-between p-3 rounded-md"
                style={{ backgroundColor: `${primaryColor}10` }}
              >
                <div className="flex items-center gap-2">
                  <FiCalendar className="text-gray-500" />
                  <span className="text-sm text-gray-600">Last Updated</span>
                </div>
                <span className="text-sm font-medium">
                  {transactions.length > 0
                    ? transactions[0].date || transactions[0].createdAt
                    : "Never"}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-3 mt-6">
              <button
                className="w-full p-2 rounded-md text-white font-medium flex items-center justify-center gap-2 transition-colors"
                style={{ backgroundColor: secondaryColor }}
                onClick={() => handleAddMode("add")}
              >
                <FiPlus /> Record Addition
              </button>
              <button
                className="w-full p-2 rounded-md text-white font-medium flex items-center justify-center gap-2 transition-colors"
                style={{ backgroundColor: primaryColor }}
                onClick={() => handleAddMode("issuance")}
              >
                <FiArrowUpRight /> Issue Inventory
              </button>
            </div>
          </div>

          {addMode && actionType === "add" && (
            <div className="mt-4 bg-white p-5 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4 flex items-center gap-2">
                <FiPlus style={{ color: secondaryColor }} /> Add Inventory
              </h3>
              <InventoryFormAdd id={data.id} setAddMode={setAddMode} />
            </div>
          )}

          {issuanceMode && actionType === "issuance" && (
            <div className="mt-4 bg-white p-5 rounded-lg shadow-sm border border-gray-100">
              <h3 className="text-sm font-semibold text-gray-600 uppercase mb-4 flex items-center gap-2">
                <FiArrowUpRight style={{ color: primaryColor }} /> Issue
                Inventory
              </h3>
              <form className="space-y-4" onSubmit={handleIssuanceSubmit}>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 mb-4 rounded-r-md">
                  <p className="text-xs text-yellow-800">
                    Available quantity:{" "}
                    <span className="font-bold">{ledgerData.balance}</span>{" "}
                    units
                  </p>
                </div>

                <InputBox
                  label="Quantity"
                  type="number"
                  name="quantity"
                  placeholder="Enter quantity to issue"
                  onChange={handleChange}
                  disabled={isProcessing}
                  required
                />

                <div className="space-y-1.5">
                  {loadingUsers ? (
                    <div className="text-center py-2">
                      <div className="animate-pulse flex items-center justify-center">
                        <div className="h-4 w-4 bg-gray-200 rounded-full mr-2"></div>
                        <span className="text-xs text-gray-500">
                          Loading users...
                        </span>
                      </div>
                    </div>
                  ) : (
                    <SelectInput
                      label="Issued To"
                      name="issuedTo"
                      options={adminUsers.map((user) => ({
                        value: user.email,
                        label: user.displayName || user.email,
                      }))}
                      isMulti
                      onChange={(selected) => setIssuedToUsers(selected || [])}
                      value={issuedToUsers}
                      disabled={isProcessing}
                      required
                    />
                  )}
                </div>

                <InputBox
                  label="Issue Date"
                  type="date"
                  name="date"
                  onChange={handleChange}
                  disabled={isProcessing}
                  required
                />
                <InputBox
                  label="Purpose"
                  type="text"
                  name="purpose"
                  placeholder="Enter purpose of issuance"
                  onChange={handleChange}
                  disabled={isProcessing}
                />
                <InputBox
                  label="Remarks"
                  type="text"
                  name="remarks"
                  placeholder="Enter any additional notes"
                  onChange={handleChange}
                  disabled={isProcessing}
                />

                <div className="flex justify-between gap-3 pt-2">
                  <button
                    type="button"
                    className="p-2 rounded-md border border-gray-200 text-gray-600 font-medium flex items-center justify-center gap-2 transition-colors w-1/2"
                    onClick={() => setIssuanceMode(false)}
                    disabled={isProcessing}
                  >
                    <FiX size={16} /> Cancel
                  </button>
                  <button
                    type="submit"
                    className="p-2 rounded-md text-white font-medium flex items-center justify-center gap-2 transition-colors w-1/2"
                    style={{ backgroundColor: primaryColor }}
                    disabled={isProcessing}
                  >
                    {isProcessing ? (
                      <span className="flex items-center gap-2">
                        <svg
                          className="animate-spin h-4 w-4 text-white"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <FiSend size={16} /> Issue Item
                      </span>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        <div className="lg:w-2/3 w-full">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden">
            <div className="flex border-b">
              <button
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 ${
                  activeTab === "transactions"
                    ? "border-b-2 text-gray-800"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  borderColor:
                    activeTab === "transactions"
                      ? secondaryColor
                      : "transparent",
                }}
                onClick={() => setActiveTab("transactions")}
              >
                <FiList size={16} /> Transactions
              </button>
              <button
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 ${
                  activeTab === "ledger"
                    ? "border-b-2 text-gray-800"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  borderColor:
                    activeTab === "ledger" ? secondaryColor : "transparent",
                }}
                onClick={() => setActiveTab("ledger")}
              >
                <FiClipboard size={16} /> Ledger
              </button>
              <button
                className={`px-4 py-3 text-sm font-medium flex items-center gap-2 ${
                  activeTab === "chart"
                    ? "border-b-2 text-gray-800"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                }`}
                style={{
                  borderColor:
                    activeTab === "chart" ? secondaryColor : "transparent",
                }}
                onClick={() => setActiveTab("chart")}
              >
                <FiBarChart2 size={16} /> Analytics
              </button>
            </div>

            <div className="p-4">{renderTabContent()}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
